import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnChanges,
  signal,
  SimpleChanges,
  TemplateRef,
  viewChild
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { DealFinancialSummaryComponent } from './financial-summary/deal-financial-summary.component';
import { DealDepositsTableComponent } from './deposits-table/deal-deposits-table.component';
import {
  CreateDeposit,
  CreateDepositPayment,
  DepositService,
  ImportsModelComponent,
  ProjectDepositScheduleService,
  importDeposit,
  SalesApsService
} from '@zayan-erp/sales';
import {
  AbilityService,
  ApiCallingV2Service,
  ConfirmHelperService,
  DropdownData,
  NgbModalService,
  SvgIconsComponent
} from '@zayan-erp/shared';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { selectProjects } from '@zayan-erp/stores';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-deal-deposits',
  standalone: true,
  imports: [
    DealDepositsTableComponent,
    DealFinancialSummaryComponent,
    SvgIconsComponent,
    ImportsModelComponent,
    NgbDropdownModule
  ],
  host: { class: 'd-flex flex-column h-100 overflow-hidden' },
  templateUrl: './deal-deposits.component.html'
})
export class DealDepositsComponent implements OnChanges {
  _depositService = inject(DepositService);
  #confirmService = inject(ConfirmHelperService);
  #store = inject(Store);
  #depositScheduleService = inject(ProjectDepositScheduleService);
  #salesApsService = inject(SalesApsService);
  #destroyRef = inject(DestroyRef);
  #abilityService = inject(AbilityService);
  #api = inject(ApiCallingV2Service);
  importType = signal<string>('lots');
  private readonly modalConfig = inject(NgbModalService);
  private readonly modal = inject(NgbModal);
  importsModal = viewChild.required<TemplateRef<any>>('importsModal');

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));

  effectiveDeal = computed(() => this.#salesApsService.currentDeal() ?? null);
  effectiveLotId = computed(() => this.effectiveDeal()?.lot.id ?? '');
  projects$ = this.#store.select(selectProjects);
  projects = toSignal(this.projects$, { initialValue: [] });

  /** Project id for the project that matches the deal's lot projectCode. Reactive: runs when deal input becomes available. */
  getProjectId = rxResourceFixed({
    params: () => ({ dealId: this.#salesApsService.currentDeal()?.id ?? '' }),
    defaultValue: '',
    stream: ({ params }) => {
      if (!params.dealId) {
        return of('');
      }
      const currentDeal = this.#salesApsService.currentDeal();
      if (!currentDeal?.project.projectCode) return of('');
      const projectCode = currentDeal.project.projectCode;
      const project = this.projects().find((p: DropdownData) => p.firstLabel === projectCode);
      return of(project?.id ?? '');
    }
  });
  projectId = computed(() => {
    return this.getProjectId.value() ?? '';
  });

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    this._depositService.dealId.set(this.#salesApsService.currentDeal()?.id ?? '');
    this._depositService.lotId.set(this.effectiveLotId());
  }

  getDeposits = rxResourceFixed({
    params: () => ({ dealId: this.effectiveDeal()?.id ?? '' }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.dealId) {
        return of([]);
      }
      return this._depositService.readDepositsByDealId(params.dealId);
    }
  });

  deposits = computed(() => this.getDeposits.value() ?? []);

  #showError(error: HttpErrorResponse, fallback: string): void {
    this.#api.showMessage(error.error?.errors?.[0] ?? error.error?.message ?? fallback, 'error');
  }

  onDepositFormSubmitted(): void {
    if (!this.canWriteDeals()) return;
    this._depositService.form.markAllAsTouched();
    if (this._depositService.form.invalid) {
      return;
    }

    this._depositService.form.patchValue({
      dealId: this.#salesApsService.currentDeal()?.id ?? '',
      lotId: this.effectiveLotId()
    });

    if (this._depositService.depositId) {
      this._depositService
        .updateDeposit(this._depositService.depositId, this._depositService.form.getRawValue() as any)
        .subscribe({
          next: () => {
            this.reset();
            this.#salesApsService.realoadDeal.next(null);
          }
        });
    } else {
      this._depositService.createDeposit([this._depositService.form.getRawValue()] as CreateDeposit[]).subscribe({
        next: () => {
          this.reset();
          this.#salesApsService.realoadDeal.next(null);
        }
      });
    }
  }

  onDepositPaymentFormSubmitted(event: { depositId: string; paymentId?: string }): void {
    if (!this.canWriteDeals()) return;
    this._depositService.depositPaymentForm.markAllAsTouched();
    if (this._depositService.depositPaymentForm.invalid) {
      return;
    }

    const { depositId, paymentId } = event;

    if (paymentId) {
      this._depositService
        .updateDepositPayment(
          depositId,
          paymentId,
          this._depositService.depositPaymentForm.getRawValue() as CreateDepositPayment
        )
        .subscribe({
          next: () => {
            this.reset();
          }
        });
    } else {
      this._depositService.depositPaymentForm.patchValue({
        depositId: depositId
      });
      this._depositService
        .createDepositPayment(depositId, this._depositService.depositPaymentForm.getRawValue() as CreateDepositPayment)
        .subscribe({
          next: () => {
            this.reset();
          }
        });
    }
  }

  reset() {
    this._depositService.depositPaymentForm.reset();
    this.getDeposits.reload();
    this._depositService.form.reset();
    this._depositService.addPayment = false;
    this._depositService.depositId = null;
    this._depositService.paymentId = null;
    this._depositService.addNewDeposits = false;
    this._depositService.deposits.set([]);
    this.getSummary.reload();
  }

  getSummary = rxResourceFixed({
    params: () => ({ dealId: this.effectiveDeal()?.id ?? '' }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.dealId) {
        return of([]);
      }
      return this._depositService.getSummaryByDeal(params.dealId);
    }
  });

  summary = computed(() => this.getSummary.value() ?? []);

  onDeleteDeposit(depositId: string): void {
    if (!this.canWriteDeals()) return;
    this._depositService.deleteDeposit(depositId).subscribe({
      next: () => {
        this.reset();
      }
    });
  }

  onBulkDeleteDeposits(depositIds: string[]): void {
    if (!this.canWriteDeals()) return;
    if (!depositIds.length) return;
    const deleteObservables = depositIds.map(id => this._depositService.deleteDeposit(id));
    forkJoin(deleteObservables).subscribe({
      next: () => {
        this.reset();
      }
    });
  }

  onDeleteDepositPayment(paymentId: string): void {
    if (!this.canWriteDeals()) return;
    this._depositService.deleteDepositPayment(paymentId).subscribe({
      next: () => {
        this.reset();
      }
    });
  }

  onConfirmDeleteDeposit(): void {
    if (!this.canWriteDeals()) return;
    this.#confirmService
      .showModal({
        icon: 'confirm-icon',
        header: 'Delete Deposit',
        message: 'Are you sure you want to delete this deposit?',
        acceptLabel: 'Delete',
        acceptSeverity: 'danger',
        rejectLabel: 'Cancel'
      })
      .subscribe(confirmed => {
        if (confirmed && this._depositService.depositId) {
          this.onDeleteDeposit(this._depositService.depositId);
        }
      });
  }

  onConfirmDeleteDepositPayment(): void {
    if (!this.canWriteDeals()) return;
    this.#confirmService
      .showModal({
        icon: 'confirm-icon',
        header: 'Delete Deposit Payment',
        message: 'Are you sure you want to delete this deposit payment?',
        acceptLabel: 'Delete',
        acceptSeverity: 'danger',
        rejectLabel: 'Cancel'
      })
      .subscribe(confirmed => {
        if (confirmed && this._depositService.paymentId) {
          this.onDeleteDepositPayment(this._depositService.paymentId);
        }
      });
  }

  close(): void {
    this._depositService.deposits.set([]);
    this.modal.dismissAll();
    this.getDeposits.reload();
  }

  openImportsModal(importType: string | null): void {
    if (!this.canWriteDeals()) return;
    this.importType.set(importType ?? 'lots');
    if (importType === 'projects') {
      this.getProjectId.reload();
      this.#loadProjectDeposits().pipe(take(1), takeUntilDestroyed(this.#destroyRef)).subscribe();
    } else {
      this.modal.open(this.importsModal(), { ...this.modalConfig.centerModal, size: 'xl' });
    }
  }

  /** Resolves projectId using same logic as getProjectId loader (deal + projects). */
  #resolveProjectIdForImport(): string {
    const currentDeal = this.#salesApsService.currentDeal();
    if (!currentDeal?.project.projectCode) return '';
    const projectCode = currentDeal.project.projectCode;
    const project = this.projects().find((p: DropdownData) => p.firstLabel === projectCode);
    return project?.id ?? '';
  }

  /** Gets projectId, schedule data (for depositScheduleId), then generates deposit previews and sets deposits. */
  #loadProjectDeposits() {
    return of(this.#resolveProjectIdForImport())
      .pipe(
        switchMap(projectId => {
          if (!projectId) return of(null);
          // ProjectDepositScheduleService: get schedule structure to read first depositScheduleId
          return this.#depositScheduleService.getDepositsByProject(projectId).pipe(
            map(data => {
              const schedules = Array.isArray(data) ? data : [data];
              const firstRow = schedules[0]?.rows?.[0] as { depositScheduleId?: string } | undefined;
              return { projectId, depositScheduleId: firstRow?.depositScheduleId ?? '' };
            })
          );
        }),
        switchMap(result => {
          if (!result?.projectId || !result.depositScheduleId) return of(null);
          const generate = (
            confirmDuplicate: boolean
          ): Observable<{ projectId: string; depositScheduleId: string; data: importDeposit[] } | null> =>
            // DepositService: generate deposit previews for this lot + schedule (different API)
            this._depositService
              .generateDeposits(
                result.projectId,
                this.effectiveLotId() ?? '',
                this.#salesApsService.currentDeal()?.id ?? '',
                result.depositScheduleId,
                confirmDuplicate
              )
              .pipe(
                map(data => ({ ...result, data })),
                catchError((error: HttpErrorResponse) => {
                  if (error.status !== 409 || confirmDuplicate) {
                    this.#showError(error, 'Could not generate the deposit schedule.');
                    return of(null);
                  }
                  return this.#confirmService
                    .showModal({
                      icon: 'confirm-icon',
                      header: 'Generate another schedule?',
                      message: 'This deal already has deposits. Generating the schedule again adds a second set.',
                      acceptLabel: 'Generate',
                      acceptSeverity: 'primary',
                      rejectLabel: 'Cancel'
                    })
                    .pipe(switchMap(confirmed => (confirmed ? generate(true) : of(null))));
                })
              );
          return generate(false);
        })
      )
      .pipe(
        take(1),
        map(result => {
          if (!result?.data) return;
          const raw =
            (Array.isArray(result.data)
              ? result.data
              : (result.data as { depositPreviews?: unknown[] })?.depositPreviews) ?? [];
          const deposits: importDeposit[] = (
            raw as Array<{
              depositTypeId: string;
              depositTypeName: string;
              amount: number;
              calculatedDueDate?: string;
              baseDate?: string;
              taskId: string;
              taskName?: string;
              depositScheduleRowId?: string;
              offsetBusinessDays?: number;
              sortOrder?: number;
            }>
          ).map(row => {
            const dueDateStr = row.calculatedDueDate ?? row.baseDate ?? '';
            return {
              lotId: this.effectiveLotId() ?? '',
              dealId: this.#salesApsService.currentDeal()?.id ?? '',
              addendumId: '',
              taskId: row.taskId,
              depositType: {
                id: (result.data as any)?.depositTypeId ?? '',
                name: (result.data as any)?.depositTypeName ?? ''
              },
              depositDueDate: dueDateStr ? new Date(dueDateStr) : (null as unknown as string),
              depositAmount: row.amount ?? 0,
              notes: '',
              depositScheduleRowId: row.depositScheduleRowId,
              offsetBusinessDays: row.offsetBusinessDays,
              sortOrder: row.sortOrder
            } as unknown as importDeposit;
          });
          this._depositService.deposits.set(deposits);
          this.getDeposits.reload();
        })
      );
  }

  onSelectedLot(lotId: string): void {
    this._depositService.earnestByLotId(lotId).subscribe({
      next: (data: any) => {
        // Convert date strings to Date objects for the datepicker
        const depositsWithDates = data.map((deposit: any) => ({
          ...deposit,
          depositDueDate: deposit.depositDueDate ? new Date(deposit.depositDueDate) : null,
          depositTypeId: deposit.depositType.id
        }));
        this._depositService.deposits.set(depositsWithDates);
      }
    });
  }

  onImportData(): void {
    if (!this.canWriteDeals()) return;
    const deposits = this._depositService.deposits();
    const depositsToSave = deposits.map((deposit: any) => ({
      lotId: deposit.lotId,
      dealId: this.#salesApsService.currentDeal()?.id ?? '',
      addendumId: deposit.addendumId,
      taskId: deposit.taskId,
      depositTypeId: deposit.depositType.id,
      depositDueDate: deposit.depositDueDate,
      depositAmount: deposit.depositAmount,
      notes: deposit.notes
    }));

    this._depositService.createDeposit(depositsToSave as CreateDeposit[]).subscribe({
      next: () => {
        this.reset();
        this.close();
      }
    });
  }

  onImportDataFromProjects = rxResourceFixed({
    params: () => ({ projectId: this.projectId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectId) {
        return of([]);
      }
      return this.#depositScheduleService.getDepositsByProject(params.projectId);
    }
  });

  /** First depositScheduleId from the first schedule's first row (from project deposit schedule API). */
  depositScheduleId = computed(() => {
    const data = this.onImportDataFromProjects.value();
    if (data == null) return '';
    const schedules = Array.isArray(data) ? data : [data];
    const firstRow = schedules[0]?.rows?.[0] as { depositScheduleId?: string } | undefined;
    return firstRow?.depositScheduleId ?? '';
  });
}
